import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, CheckCircle2, CircleDashed, Loader2, Mail, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/landing/Reveal";
import { api } from "@/lib/api";

export default function Confirmation() {
    const { orderId } = useParams();
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let timer;
        let stopped = false;
        const load = () => {
            api.get(`/orders/${orderId}`)
                .then(({ data }) => {
                    if (stopped) return;
                    setOrder(data);
                    if (data.payment_status === "awaiting_payment") {
                        timer = setTimeout(load, 3000);
                    }
                })
                .catch(() => !stopped && setError(true));
        };
        load();
        return () => {
            stopped = true;
            clearTimeout(timer);
        };
    }, [orderId]);

    const awaiting = order?.payment_status === "awaiting_payment";

    return (
        <div className="min-h-screen bg-[#FAFBFD]" data-testid="order-confirmation-container">
            <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-md">
                <div className="mx-auto flex h-16 max-w-3xl items-center justify-center px-4">
                    <Link to="/" data-testid="confirmation-logo-link">
                        <Logo />
                    </Link>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-4 py-14 sm:py-20">
                {error ? (
                    <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="confirmation-error">
                        <p className="font-display text-xl font-bold text-slate-900">Order not found</p>
                        <p className="mt-2 text-sm text-slate-500">
                            Check the link from your confirmation email, or contact{" "}
                            <a href="mailto:support@getitf.xyz" className="font-semibold text-emerald-700">support@getitf.xyz</a>.
                        </p>
                        <Link to="/" className="mt-6 inline-block rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white">
                            Back to home
                        </Link>
                    </div>
                ) : !order ? (
                    <div className="flex justify-center py-24">
                        <Loader2 className="h-8 w-8 animate-spin text-slate-300" />
                    </div>
                ) : (
                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <div className="text-center">
                            <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.15 }}
                                className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${awaiting ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"}`}
                            >
                                {awaiting ? <Loader2 className="h-8 w-8 animate-spin" /> : <Check className="h-8 w-8" strokeWidth={3} />}
                            </motion.span>
                            <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
                                {awaiting ? "Confirming your payment…" : "Payment received — access is on the way."}
                            </h1>
                            <p className="mx-auto mt-3 max-w-lg text-base text-slate-600">
                                {awaiting ? (
                                    <>Dodo Payments is processing your payment. This page updates automatically the moment it clears — usually a few seconds.</>
                                ) : (
                                    <>
                                        Thank you, <span className="font-semibold text-slate-900">{order.full_name}</span>. We are verifying
                                        your order now and will activate ITF on your TradingView account — usually within a few hours.
                                    </>
                                )}
                            </p>
                        </div>

                        <div className="mt-10 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8" data-testid="confirmation-order-card">
                            <div className="flex items-center justify-between">
                                <h2 className="font-display text-base font-bold text-slate-900">Order {order.order_id}</h2>
                                <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider ${awaiting ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                                    {awaiting ? "Confirming" : order.payment_status}
                                </span>
                            </div>
                            <dl className="mt-5 grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                                <div className="flex justify-between sm:block">
                                    <dt className="text-slate-500">Plan</dt>
                                    <dd className="font-semibold text-slate-900">{order.plan}</dd>
                                </div>
                                <div className="flex justify-between sm:block">
                                    <dt className="text-slate-500">Amount</dt>
                                    <dd className="font-semibold text-slate-900">$275.00 USD (one-time)</dd>
                                </div>
                                <div className="flex justify-between sm:block">
                                    <dt className="text-slate-500">TradingView username</dt>
                                    <dd className="font-mono text-[13px] font-semibold text-slate-900">{order.tradingview_username}</dd>
                                </div>
                                <div className="flex justify-between sm:block">
                                    <dt className="text-slate-500">Access valid until</dt>
                                    <dd className="font-semibold text-slate-900">
                                        {new Date(order.expires_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        {!awaiting && (
                            <>
                                <div className="mt-6 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8">
                                    <h2 className="font-display text-base font-bold text-slate-900">What happens next</h2>
                                    <ol className="mt-6 space-y-0">
                                        {[
                                            { icon: CheckCircle2, state: "done", title: "Payment received", body: "Your order is confirmed and logged." },
                                            {
                                                icon: CircleDashed,
                                                state: "current",
                                                title: "Verification & activation",
                                                body: `We verify the order and grant ITF to @${order.tradingview_username} through TradingView's invite system — usually within a few hours.`,
                                            },
                                            {
                                                icon: CircleDashed,
                                                state: "next",
                                                title: "ITF live on your chart",
                                                body: "Open TradingView → Indicators → Invite-only scripts → Institutional Trend Flow. Done.",
                                            },
                                        ].map((s, i) => (
                                            <li key={s.title} className="relative flex gap-4 pb-7 last:pb-0">
                                                {i < 2 && <span className="absolute left-[15px] top-8 h-full w-px bg-slate-200" />}
                                                <span
                                                    className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                                                        s.state === "done"
                                                            ? "bg-emerald-100 text-emerald-600"
                                                            : s.state === "current"
                                                              ? "bg-slate-900 text-emerald-400"
                                                              : "bg-slate-100 text-slate-400"
                                                    }`}
                                                >
                                                    <s.icon className={`h-4 w-4 ${s.state === "current" ? "animate-pulse-dot" : ""}`} />
                                                </span>
                                                <div>
                                                    <p className={`font-display text-sm font-bold ${s.state === "next" ? "text-slate-400" : "text-slate-900"}`}>{s.title}</p>
                                                    <p className="mt-1 text-sm leading-relaxed text-slate-500">{s.body}</p>
                                                </div>
                                            </li>
                                        ))}
                                    </ol>
                                </div>

                                {order.email_sent && (
                                    <div className="mt-6 flex items-start gap-3 rounded-2xl border border-emerald-200/70 bg-emerald-50/60 p-5 text-sm text-slate-700">
                                        <Mail className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                                        <p>
                                            A confirmation email has been sent to <span className="font-semibold text-slate-900">{order.email}</span> with
                                            your order details and activation instructions.
                                        </p>
                                    </div>
                                )}
                            </>
                        )}

                        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                            <Link
                                to="/"
                                data-testid="confirmation-home-button"
                                className="rounded-full bg-slate-900 px-7 py-3 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:scale-[1.02] hover:bg-slate-800"
                            >
                                Back to home
                            </Link>
                            <p className="flex items-center gap-1.5 text-xs text-slate-400">
                                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> We will never ask for your TradingView password.
                            </p>
                        </div>
                    </motion.div>
                )}
            </main>
        </div>
    );
}
